import { lazy } from "react";

// project import
import Loadable from "@shared/components/Loadable";
import MainLayout from "layout/MainLayout";
import { useLocation } from "react-router";

const NoAccessPage = Loadable(lazy(() => import("@apps/AuthApp/pages/NoAccessPage")));

// ============================================
// IMPORTS - Fully migrated to @apps modular structure
// All legacy paths have been removed
// ============================================

// Assets - using new modular structure
const BreakdownData = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/BreakdownData"))
);
const Machine = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/Machine"))
);
const MachineConfiguration = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/MachineConfiguration"))
);
const AssetDetails = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/AssetDetails"))
);
const AssetAnalytics = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/AssetAnalytics"))
);
const AssetAnalytics2 = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/AssetAnalytics2"))
);
const Calender = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/Calender"))
);

const Report = Loadable(
  lazy(() => import("@apps/AssetsApp/pages/Report"))
);

// Spares - using new modular structure
const SpareParts = Loadable(
  lazy(() => import("@apps/SparesApp/pages/SpareManagement"))
);
const SpareInward = Loadable(
  lazy(() => import("@apps/SparesApp/pages/SpareInward"))
);
const SpareOutward = Loadable(
  lazy(() => import("@apps/SparesApp/pages/SpareOutward"))
);
const SpareAnalytics = Loadable(
  lazy(() => import("@apps/SparesApp/pages/SpareAnalytics"))
);
const ShowSpareAnalysis = Loadable(
  lazy(() => import("@apps/SparesApp/pages/ShowSpareAnalysisData"))
);

// Tickets - using new modular structure
const Tickets = Loadable(
  lazy(() => import("@apps/TicketsApp/pages/Tickets"))
);
const TicketDetails = Loadable(
  lazy(() => import("@apps/TicketsApp/pages/TicketDetails"))
);
const TimeLineComponent = Loadable(
  lazy(() => import("@apps/TicketsApp/pages/TimeLineComponent"))
);
const MaintenanceTicket = Loadable(
  lazy(() => import("@apps/MaintenanceApp/pages/Ticket"))
);
const Ticket = Loadable(
  lazy(() => import("@apps/BreakdownApp/pages/Ticket"))
);
const CalibrationTicket = Loadable(
  lazy(() => import("@apps/CalibrationApp/pages/Ticket"))
);

// Configuration - using new modular structure
const TicketWorkFlow = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/TicketWorkFlow"))
);
const TicketWorkFlow2 = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/TicketWorkFlow2"))
);
const UserConfig = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/UserConfig"))
);
const AccessControl = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/AccessControl"))
);
const AddLocation = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/AddLocation"))
);
const SpareConfiguration = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/SpareConfigurations"))
);
const AssetConfiguration = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/AssetConfiguration"))
);
const DesignationHeirarchy = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/DesignationHeirarchy"))
);
const ManufacturerConfig = Loadable(
  lazy(() => import("@apps/ConfigurationApp/pages/ManufacturerConfig"))
);

// Checklist - using new modular structure
const Checklist = Loadable(
  lazy(() => import("@apps/ChecklistApp/pages/ChecklistTabs"))
);
const ChecklistTicket = Loadable(
  lazy(() => import("@apps/ChecklistApp/pages/ChecklistTicket"))
);

// Audit - using new modular structure
const AuditForm = Loadable(
  lazy(() => import("@apps/AuditApp/pages/AuditForm"))
);
const AuditTicket = Loadable(
  lazy(() => import("@apps/AuditApp/pages/AuditTicket"))
);
const AuditReport = Loadable(
  lazy(() => import("@apps/AuditApp/pages/AuditReport"))
);

// Vendor - using new modular structure
const VendorRegistration = Loadable(
  lazy(() => import("@apps/VendorApp/pages/VendorRegistration"))
);
const VendorReg = Loadable(
  lazy(() => import("@apps/VendorApp/pages/RegisterVendor"))
);
const Vendordetails = Loadable(
  lazy(() => import("@apps/VendorApp/pages/ViewVisitor"))
);
const VendorApproval = Loadable(
  lazy(() => import("@apps/VendorApp/pages/ApproveVendor"))
);
const ViewCheckList = Loadable(
  lazy(() => import("@apps/VendorApp/pages/ViewChecklist"))
);

// Dashboard - using new modular structure
const DashboardDefault = Loadable(lazy(() => import("@apps/DashboardApp/pages/index")));
const MainDashboard = Loadable(lazy(() => import("@apps/DashboardApp/pages/MainDashboard")));

// Company - using new modular structure
const CompanyProfileForm = Loadable(
  lazy(() => import("@apps/CompanyApp/pages/CompanyProfileForm"))
);

// Reports - using new modular structure
const ReportIndex = Loadable(
  lazy(() => import("@apps/ReportsApp/pages/ReportIndex"))
);

// Shared Components
const UserProfile = Loadable(lazy(() => import("@shared/components/UserProfile")));
const NotificationPage = Loadable(lazy(() => import("@shared/components/NotificationPage")));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = (access_list_data) => {
  const use_location = useLocation();
  const machine_id =
    use_location.pathname.match(/\d+/) &&
    parseInt(use_location.pathname.match(/\d+/)[0], 10);

  const get_access = (module_name, access_type) => {
    return Boolean(
      access_list_data?.find((item) => item.module_name === module_name)
    )
      ? access_list_data?.find((item) => item.module_name === module_name)[
          access_type
        ]
      : false;
  };

  return {
    path: "/",
    element: <MainLayout />,
    children: [
      {
        path: "/",
        element: <DashboardDefault />,
      },
      get_access("Dashboard", "read_access") && {
        path: "/dashboard",
        element: <DashboardDefault />,
      },
      get_access("Dashboard", "read_access") && {
        path: "/main-dashboard",
        element: <MainDashboard />,
      },
      get_access("Tickets", "read_access") && {
        path: "/tickets",
        element: <Tickets />,
      },
      get_access("Assets", "create_access") && {
        path: "/add-asset",
        element: <MachineConfiguration />,
      },
      get_access("Assets", "read_access") && {
        path: "/asset",
        element: <Machine />,
      },
      get_access("Assets", "read_access") && {
        path: "/asset-report",
        element: <Report />,
      },
      get_access("Tickets", "create_access") && {
        path: "/create-ticket",
        element: <TicketDetails />,
      },
      get_access("Ticket Work Flow", "read_access") && {
        path: "/ticket-work-flow",
        element: <TicketWorkFlow />,
      },
      get_access("Ticket Work Flow", "read_access") && {
        path: "/ticket-work-flow2",
        element: <TicketWorkFlow2 />,
      },
      get_access("Tickets", "read_access") && {
        path: "/ticket-details",
        element: <TimeLineComponent />,
      },
      get_access("User Configuration", "read_access") && {
        path: "/user-configuration",
        element: <UserConfig />,
      },
      get_access("Tickets", "read_access") && {
        // Notifications are entirely ticket-driven — gate on the same
        // Tickets access flag used for the header bell icon and the
        // /calendar route. Without Tickets read access, direct URL hits
        // fall through to the catchall NoAccessPage.
        path: "/notifications",
        element: <NotificationPage />,
      },
      get_access("Access Control", "read_access") && {
        path: "/access-control",
        element: <AccessControl />,
      },
      get_access("Location Configuration", "read_access") && {
        path: "/location-configuration",
        element: <AddLocation />,
      },
      {
        path: "/user-profile",
        element: <UserProfile />,
      },
      get_access("Visitor Details", "read_access") && {
        path: "/registerVendor",
        element: <VendorReg />,
      },
      get_access("Visitor Details", "read_access") && {
        path: "/visitor-details",
        element: <Vendordetails />,
      },
      get_access("Visitor Approvals", "read_access") && {
        path: "/approveVendor",
        element: <VendorApproval />,
      },
      get_access("Visitor Approvals", "read_access") && {
        path: "/visitor-checklist",
        element: <ViewCheckList />,
      },
      get_access("Designation Hierarchy", "read_access") && {
        path: "/designation-hierarchy",
        element: <DesignationHeirarchy />,
      },
      get_access("Tickets", "read_access") && {
        // Calendar is purely a ticket view (shows scheduled/completed/
        // delayed tickets and navigates into ticket details on click), so
        // gate it on the same access flag as the rest of the Tickets module.
        path: "/calendar",
        element: <Calender />,
      },
      get_access("Tickets", "read_access") && {
        path: "/maintenance-data",
        element: <MaintenanceTicket />,
      },
      get_access("Tickets", "read_access") && {
        path: "/break-down",
        element: <Ticket />,
      },
      get_access("Tickets", "read_access") && {
        path: "/calibration-check",
        element: <CalibrationTicket />,
      },
      get_access("Visitor Details", "read_access") && {
        path: "/register-vendor",
        element: <VendorRegistration />,
      },
      get_access("Company Details", "read_access") && {
        path: "/company-profile",
        element: <CompanyProfileForm />,
      },
      get_access("Spares", "read_access") && {
        path: "/spare-parts",
        element: <SpareParts />,
      },
      get_access("Inward", "read_access") && {
        path: "/spare-inward",
        element: <SpareInward />,
      },
      {
        path: "/spare-outward",
        element: <SpareOutward />,
      },
      {
        path: "/spare-configuration",
        element: <SpareConfiguration />,
      },
      {
        path: "/asset-configuration",
        element: <AssetConfiguration />,
      },
      get_access("Spare Analytics", "read_access") && {
        path: "/spare-analytics",
        element: <SpareAnalytics />,
      },
      get_access("Spare Analytics", "read_access") && {
        path: "/spare-analytics-data",
        element: <ShowSpareAnalysis />,
      },
      get_access("Assets", "read_access") && {
        path: `/asset/${machine_id}`,
        element: <AssetDetails />,
      },
      get_access("Analytics", "read_access") && {
        path: machine_id ? `/analytics/${machine_id}` : "/analytics",
        element: <AssetAnalytics2 />,
      },
      get_access("Asset Analytics", "read_access") && {
        path: machine_id ? `/asset-analytic/${machine_id}` : `/asset-analytic`,
        element: <AssetAnalytics />,
      },
      get_access("Checklist", "read_access") && {
        path: `/checklist`,
        element: <Checklist />,
      },
      get_access("Report", "read_access") && {
        path: "/report",
        element: <ReportIndex />,
      },
      {
        path: "/vendor-configuration",
        element: <ManufacturerConfig />,
      },
      {
        path: `/temp`,
        element: <ChecklistTicket />,
      },
      get_access("Audit", "read_access") && {
        path: `/audit`,
        element: <AuditForm />,
      },
      {
        path: `/audit-ticket`,
        element: <AuditTicket />,
      },
      get_access("Audit", "read_access") && {
        path: `/audit-report`,
        element: <AuditReport />,
      },
      {
        // Stable landing for users whose token is valid but whose role
        // grants zero module access (or whose access list hasn't loaded
        // yet). The login flow redirects here as the last fallback so
        // the user sees the Access Denied screen with chrome instead of
        // a blank page.
        path: "/no-access",
        element: <NoAccessPage />,
      },
      {
        path: "*",
        element: <NoAccessPage />,
      },
    ]?.filter(Boolean),
  };
};

export default MainRoutes;
